import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Button } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MapIcon from "@mui/icons-material/Map";

mapboxgl.accessToken =
  "pk.eyJ1IjoiYWJoaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA";

const ParcelTableWithMap = () => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  // State
  const [parcelData, setParcelData] = useState([]);
  const [filteredParcels, setFilteredParcels] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  // View State
  const [showMap, setShowMap] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);

  // Filter States
  const [showCountyFilter, setShowCountyFilter] = useState(false);
  const [countyFilter, setCountyFilter] = useState("All");
  const [countyOptions, setCountyOptions] = useState(["All"]);
  
  const [showOnlyVacant, setShowOnlyVacant] = useState(false);
  const [showOnlyListed, setShowOnlyListed] = useState(false);

  const FLOOD_ZONE_LEGEND = [
    { zone: "X", color: "#4CAF50", label: "X - Minimal Risk" },
    { zone: "AE", color: "#FF9800", label: "AE - High Risk + BFE" },
    { zone: "VE", color: "#E91E63", label: "VE - Coastal + BFE" },
    { zone: "A", color: "#2196F3", label: "A - High Risk (No BFE)" },
    { zone: "null", color: "#9fa2e7", label: "Unknown" },
  ];

  // Helper functions
  const normalizeListed = (raw) => {
    if (raw === null || raw === undefined) return "Not listed";
    const s = String(raw).trim().replace(/\s/g, " ");
    return s === "" ? "Not listed" : s;
  };

  const getListedRaw = (props = {}) => {
    return (
      props.listedsale ??
      props.listed_sale ??
      props.listedSale ??
      props.ListedSale ??
      props.listingsale ??
      props.listedsales ??
      null
    );
  };

  const isCountyActive = () => countyFilter && countyFilter !== "All";

  const formatCurrency = (val) => {
    if (!val) return "N/A";
    return `$${Number(val).toLocaleString()}`;
  };

  // ✅ Check if "Show Map" button should be enabled
  const canShowMap = () => {
    return countyFilter !== "All";
  };

  // Clear all filters
  const clearAllFilters = () => {
    console.log("🔄 Clearing all filters...");
    setCountyFilter("All");
    setShowOnlyVacant(false);
    setShowOnlyListed(false);
    setShowCountyFilter(false);
    setShowMap(false);
    console.log("✅ Filters cleared");
  };

  // ✅ Load GeoJSON data
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log("📥 Starting GeoJSON fetch...");
        const res = await fetch("/assets/parcel/county_northcarolina_parcels.geojson");
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        console.log("📦 Parsing GeoJSON...");
        const geojson = await res.json();

        const features = geojson.features || [];
        console.log(`✅ GeoJSON loaded: ${features.length} features`);

        // Extract unique counties
        console.log("🔍 Extracting counties...");
        const counties = Array.from(
          new Set(
            features.map((f) => {
              const p = f.properties || {};
              return p.county || p.County || p.COUNTY || "Unknown";
            })
          )
        ).sort();
        
        console.log(`✅ Found ${counties.length} counties:`, counties);
        setCountyOptions(["All", ...counties]);

        setParcelData(features);
        setFilteredParcels(features);

        console.log(`✅ Data loaded successfully: ${features.length} parcels`);
      } catch (error) {
        console.error("❌ Error loading GeoJSON:", error);
        console.error("Error details:", error.message);
      }
    };

    loadData();
  }, []);

  // ✅ Apply filters
  useEffect(() => {
    console.log("🔧 Applying filters...", {
      countyFilter,
      showOnlyVacant,
      showOnlyListed,
      totalParcels: parcelData.length
    });

    const filtered = parcelData.filter((f) => {
      const props = f.properties || {};

      // County filter
      const county = props.county || props.County || props.COUNTY || "Unknown";
      const matchCounty =
        countyFilter === "All" ||
        String(county).toLowerCase() === String(countyFilter).toLowerCase();

      // Vacant filter
      const isVacant = props.is_vaccant === 1 || props.is_vaccant === "1";
      const matchVacant = !showOnlyVacant || isVacant;

      // Listed filter
      const listedVal = normalizeListed(getListedRaw(props));
      const matchListed = !showOnlyListed || listedVal !== "Not listed";

      return matchCounty && matchVacant && matchListed;
    });

    console.log(`✅ Filtered to ${filtered.length} parcels`);
    setFilteredParcels(filtered);

    // ✅ Hide map if county is reset to "All"
    if (countyFilter === "All") {
      console.log("🗺️ Hiding map (county filter reset)");
      setShowMap(false);
    }

    // Update map if already initialized
    if (mapRef.current && mapInitialized) {
      console.log("🗺️ Updating map data...");
      updateMapData(filtered);
    }
  }, [parcelData, countyFilter, showOnlyVacant, showOnlyListed, mapInitialized]);

  // ✅ Initialize map when "Show Map" is clicked
  useEffect(() => {
    if (!showMap) {
      console.log("🗺️ Map hidden, skipping initialization");
      return;
    }

    if (mapInitialized) {
      console.log("🗺️ Map already initialized");
      return;
    }

    const initMap = async () => {
      try {
        console.log("🗺️ Initializing map...");
        console.log("📍 Map container:", mapContainerRef.current);

        if (!mapContainerRef.current) {
          console.error("❌ Map container ref is null!");
          return;
        }

        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: "mapbox://styles/mapbox/light-v11",
          center: [-78.6443, 35.7877],
          zoom: 10,
        });

        console.log("🗺️ Map instance created, waiting for load event...");

        mapRef.current.on("load", () => {
          console.log("🗺️ Map loaded successfully");

          try {
            // Add source
            console.log("➕ Adding parcels source...", {
              featureCount: filteredParcels.length
            });

            mapRef.current.addSource("parcels", {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: filteredParcels,
              },
            });

            console.log("✅ Parcels source added");

            // Add fill layer
            console.log("🎨 Adding fill layer...");
            mapRef.current.addLayer({
              id: "parcel-fill",
              type: "fill",
              source: "parcels",
              paint: {
                "fill-color": [
                  "match",
                  ["get", "parcelcustomfemaflood"],
                  "X", "#4CAF50",
                  "AE", "#FF9800",
                  "VE", "#E91E63",
                  "A", "#2196F3",
                  "#9fa2e7",
                ],
                "fill-opacity": 0.45,
              },
            });

            console.log("✅ Fill layer added");

            // Add outline layer
            console.log("🖊️ Adding outline layer...");
            mapRef.current.addLayer({
              id: "parcel-outline",
              type: "line",
              source: "parcels",
              paint: {
                "line-color": [
                  "match",
                  ["get", "parcelcustomfemaflood"],
                  "X", "#4CAF50",
                  "AE", "#FF9800",
                  "VE", "#E91E63",
                  "A", "#2196F3",
                  "#9fa2e7",
                ],
                "line-width": 1.3,
              },
            });

            console.log("✅ Outline layer added");

            // ✅ Popup on click - WITH DEBUG LOGGING
            mapRef.current.on("click", "parcel-fill", (e) => {
              console.log("🖱️ Parcel clicked!", {
                coordinates: e.lngLat,
                features: e.features.length,
                properties: e.features[0]?.properties
              });

              if (!e.features || e.features.length === 0) {
                console.warn("⚠️ No features found at click location");
                return;
              }

              const props = e.features[0].properties;
              console.log("📋 Parcel properties:", props);

              try {
                const popup = new mapboxgl.Popup()
                  .setLngLat(e.lngLat)
                  .setHTML(
                    `
                  <div style="background:#fff; padding:14px; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.15); font-family:Inter, sans-serif; font-size:13px; width:240px;">
                    <h3 style="margin:0 0 6px 0; color:#1565C0; font-size:15px;">${props.addrline1 || "N/A"}</h3>
                    <hr style="border:none; border-top:1px solid #eee; margin:8px 0;">
                    <p><strong>County:</strong> ${props.county || "N/A"}</p>
                    <p><strong>Parcel:</strong> ${props.parcel || "N/A"}</p>
                    <p><strong>Size:</strong> ${props.parcelsize ? props.parcelsize.toFixed(3) + " ac" : "N/A"}</p>
                    <p><strong>Zoning:</strong> ${props.zonedcodelocal || "N/A"}</p>
                    <p><strong>Flood Zone:</strong> ${props.parcelcustomfemaflood || "N/A"}</p>
                    <p><strong>Vacant:</strong> ${props.is_vaccant === 1 ? "Yes" : "No"}</p>
                    <p><strong>Listed:</strong> ${props.listedsale || "Not listed"}</p>
                    <p><strong>Price:</strong> ${formatCurrency(props.zestimate)}</p>
                  </div>
                `
                  )
                  .addTo(mapRef.current);

                console.log("✅ Popup created and added to map");
              } catch (popupError) {
                console.error("❌ Error creating popup:", popupError);
              }
            });

            // Change cursor
            mapRef.current.on("mouseenter", "parcel-fill", () => {
              console.log("🖱️ Mouse entered parcel");
              mapRef.current.getCanvas().style.cursor = "pointer";
            });

            mapRef.current.on("mouseleave", "parcel-fill", () => {
              console.log("🖱️ Mouse left parcel");
              mapRef.current.getCanvas().style.cursor = "";
            });

            setMapInitialized(true);
            console.log("✅ Map fully initialized");

            // Fit bounds to filtered parcels
            console.log("📍 Fitting map to parcels...");
            fitMapToParcels(filteredParcels);

          } catch (layerError) {
            console.error("❌ Error adding layers:", layerError);
            console.error("Layer error details:", layerError.message);
          }
        });

        // ✅ Map error handling
        mapRef.current.on("error", (e) => {
          console.error("❌ Map error event:", e);
        });

      } catch (error) {
        console.error("❌ Error initializing map:", error);
        console.error("Init error details:", error.message);
      }
    };

    initMap();

    return () => {
      console.log("🧹 Cleaning up map...");
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        setMapInitialized(false);
        console.log("✅ Map cleaned up");
      }
    };
  }, [showMap, filteredParcels]);

  // ✅ Update map data when filters change
  const updateMapData = (parcels) => {
    console.log(`🔄 Updating map data with ${parcels.length} parcels...`);
    
    try {
      if (!mapRef.current) {
        console.warn("⚠️ Map ref is null, cannot update");
        return;
      }

      const source = mapRef.current.getSource("parcels");
      if (!source) {
        console.warn("⚠️ Parcels source not found");
        return;
      }

      source.setData({
        type: "FeatureCollection",
        features: parcels,
      });

      console.log("✅ Map data updated");

      // Fit bounds to filtered parcels
      fitMapToParcels(parcels);
    } catch (e) {
      console.error("❌ Error updating map data:", e);
      console.error("Update error details:", e.message);
    }
  };

  // ✅ Fit map bounds to parcels
  const fitMapToParcels = (parcels) => {
    console.log(`📍 Fitting map bounds to ${parcels.length} parcels...`);

    if (!mapRef.current) {
      console.warn("⚠️ Map ref is null, cannot fit bounds");
      return;
    }

    if (parcels.length === 0) {
      console.warn("⚠️ No parcels to fit bounds to");
      return;
    }

    try {
      const coords = parcels
        .map((f) => {
          const p = f.properties || {};
          const lon = parseFloat(p.longitude);
          const lat = parseFloat(p.latitude);
          if (isNaN(lon) || isNaN(lat)) {
            console.log("⚠️ Invalid coordinates:", { lon, lat, parcel: p.parcel });
            return null;
          }
          return [lon, lat];
        })
        .filter(Boolean);

      console.log(`📍 Valid coordinates: ${coords.length}/${parcels.length}`);

      if (coords.length === 0) {
        console.warn("⚠️ No valid coordinates found");
        return;
      }

      const lons = coords.map((c) => c[0]);
      const lats = coords.map((c) => c[1]);

      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);

      console.log("📍 Bounds:", {
        minLon,
        maxLon,
        minLat,
        maxLat
      });

      mapRef.current.fitBounds(
        [
          [minLon, minLat],
          [maxLon, maxLat],
        ],
        {
          padding: 50,
          duration: 1500,
          maxZoom: 14,
        }
      );

      console.log("✅ Map bounds fitted");
    } catch (err) {
      console.error("❌ Error fitting map bounds:", err);
      console.error("Bounds error details:", err.message);
    }
  };

  // ✅ Handle row click - zoom to parcel on map
  const handleRowClick = (parcel, index) => {
    console.log(`📋 Row clicked: ${index}`, parcel);
    setSelectedRow(index);
    const p = parcel.properties || parcel;

    if (!showMap) {
      console.log("🗺️ Map not visible, skipping zoom");
      return;
    }

    if (!mapRef.current) {
      console.warn("⚠️ Map ref is null, cannot zoom");
      return;
    }

    const lon = parseFloat(p.longitude);
    const lat = parseFloat(p.latitude);

    if (isNaN(lon) || isNaN(lat)) {
      console.warn("⚠️ Invalid coordinates for row click:", { lon, lat });
      return;
    }

    console.log(`🎯 Flying to: [${lon}, ${lat}]`);

    try {
      mapRef.current.flyTo({
        center: [lon, lat],
        zoom: 16,
        duration: 1500,
      });

      // Show popup
      const popup = new mapboxgl.Popup()
        .setLngLat([lon, lat])
        .setHTML(
          `
        <div style="background:#fff; padding:14px; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.15); font-family:Inter, sans-serif; font-size:13px; width:240px;">
          <h3 style="margin:0 0 6px 0; color:#1565C0; font-size:15px;">${p.addrline1 || "N/A"}</h3>
          <hr style="border:none; border-top:1px solid #eee; margin:8px 0;">
          <p><strong>County:</strong> ${p.county || "N/A"}</p>
          <p><strong>Parcel:</strong> ${p.parcel || "N/A"}</p>
          <p><strong>Size:</strong> ${p.parcelsize ? p.parcelsize.toFixed(3) + " ac" : "N/A"}</p>
          <p><strong>Zoning:</strong> ${p.zonedcodelocal || "N/A"}</p>
          <p><strong>Flood Zone:</strong> ${p.parcelcustomfemaflood || "N/A"}</p>
          <p><strong>Vacant:</strong> ${p.is_vaccant === 1 ? "Yes" : "No"}</p>
          <p><strong>Listed:</strong> ${p.listedsale || "Not listed"}</p>
          <p><strong>Price:</strong> ${formatCurrency(p.zestimate)}</p>
        </div>
      `
        )
        .addTo(mapRef.current);

      console.log("✅ Popup added for row click");
    } catch (error) {
      console.error("❌ Error handling row click:", error);
      console.error("Row click error details:", error.message);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* ✅ Header with Filters */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #ddd",
          padding: "10px 15px",
          display: "flex",
          gap: "10px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* County Filter */}
        <div style={{ position: "relative" }}>
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={() => {
              console.log("🔽 County dropdown toggled");
              setShowCountyFilter(!showCountyFilter);
            }}
            style={{
              textTransform: "none",
              background: isCountyActive() ? "#1c8f7d" : "transparent",
              color: isCountyActive() ? "#fff" : "#333",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              borderRadius: 6,
              fontSize: "12px",
            }}
          >
            {countyFilter === "All" ? "County" : `County - ${countyFilter}`}
          </Button>

          {showCountyFilter && (
            <div
              style={{
                position: "absolute",
                top: "42px",
                left: 0,
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                borderRadius: "6px",
                zIndex: 999,
                padding: "6px 0",
                minWidth: "200px",
                maxHeight: "300px",
                overflow: "auto",
              }}
            >
              {countyOptions.map((county) => (
                <div
                  key={county}
                  onClick={() => {
                    console.log(`📍 County selected: ${county}`);
                    setCountyFilter(county);
                    setShowCountyFilter(false);
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    background: countyFilter === county ? "#1c8f7d" : "transparent",
                    color: countyFilter === county ? "#fff" : "#000",
                    fontSize: 13,
                  }}
                >
                  {county}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Vacant Checkbox */}
        <div
          style={{
            color: "#007c6f",
            borderRadius: "20px",
            border: "solid 1px #007c6f",
            padding: "6px 15px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
          }}
          onClick={() => {
            console.log(`☑️ Vacant filter toggled: ${!showOnlyVacant}`);
            setShowOnlyVacant(!showOnlyVacant);
          }}
        >
          <input
            type="checkbox"
            checked={showOnlyVacant}
            onChange={(e) => setShowOnlyVacant(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "18px",
              height: "18px",
              cursor: "pointer",
              accentColor: "#1c8f7d",
            }}
          />
          <span
            style={{
              fontSize: "12px",
              fontWeight: showOnlyVacant ? "600" : "500",
              color: showOnlyVacant ? "#1c8f7d" : "#333",
            }}
          >
            Vacant Only
          </span>
        </div>

        {/* Listed Sales Checkbox */}
        <div
          style={{
            color: "#007c6f",
            borderRadius: "20px",
            border: "solid 1px #007c6f",
            padding: "6px 15px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
          }}
          onClick={() => {
            console.log(`☑️ Listed filter toggled: ${!showOnlyListed}`);
            setShowOnlyListed(!showOnlyListed);
          }}
        >
          <input
            type="checkbox"
            checked={showOnlyListed}
            onChange={(e) => setShowOnlyListed(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "18px",
              height: "18px",
              cursor: "pointer",
              accentColor: "#1c8f7d",
            }}
          />
          <span
            style={{
              fontSize: "12px",
              fontWeight: showOnlyListed ? "600" : "500",
              color: showOnlyListed ? "#1c8f7d" : "#333",
            }}
          >
            Listed for Sales
          </span>
        </div>

        {/* Clear Filters */}
        <Button
          variant="outlined"
          onClick={clearAllFilters}
          style={{
            textTransform: "none",
            borderColor: "#cfcfcf",
            color: "#333",
            fontSize: "12px",
          }}
        >
          Clear Filters
        </Button>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "13px", color: "#666" }}>
            {filteredParcels.length} parcels
          </span>

          {/* ✅ Show Map Button - Only enabled when county is selected */}
          <Button
            variant="contained"
            startIcon={<MapIcon />}
            onClick={() => {
              console.log(`🗺️ Show Map toggled: ${!showMap}`);
              setShowMap(!showMap);
            }}
            disabled={!canShowMap()}
            style={{
              textTransform: "none",
              background: canShowMap() ? (showMap ? "#d32f2f" : "#1c8f7d") : "#ccc",
              color: "#fff",
              fontSize: "12px",
              cursor: canShowMap() ? "pointer" : "not-allowed",
            }}
          >
            {showMap ? "Hide Map" : "Show Map"}
          </Button>
        </div>
      </div>

      {/* ✅ Main Content Area - List on Left, Map on Right */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* ✅ Table View - Always Visible on Left */}
        <div
          style={{
            width: showMap ? "50%" : "100%",
            overflowY: "auto",
            background: "#fafafa",
            borderRight: showMap ? "1px solid #ddd" : "none",
            transition: "width 0.3s ease",
          }}
        >
          {filteredParcels.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
              No parcels found. Try adjusting filters.
            </div>
          ) : (
            <table
              style={{
                width: showMap ? "50%" : "100%",
                borderCollapse: "collapse",
                fontSize: "12px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#e0e0e0",
                    fontWeight: "bold",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <th style={{ padding: "10px", textAlign: "left" }}>Parcel</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Address</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>County</th>
                  <th style={{ padding: "10px", textAlign: "right" }}>Size (ac)</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Zoning</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Flood Zone</th>
                  <th style={{ padding: "10px", textAlign: "center" }}>Vacant</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Listed</th>
                  <th style={{ padding: "10px", textAlign: "right" }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {filteredParcels.map((feature, i) => {
                  const p = feature.properties || {};
                  const isSelected = selectedRow === i;
                  return (
                    <tr
                      key={i}
                      onClick={() => handleRowClick(feature, i)}
                      style={{
                        background: isSelected
                          ? "#dcedeb"
                          : i % 2 === 0
                          ? "#fff"
                          : "#f9f9f9",
                        cursor: "pointer",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.background = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#f9f9f9";
                      }}
                    >
                      <td style={{ padding: "10px" }}>{p.parcel || "N/A"}</td>
                      <td style={{ padding: "10px" }}>{p.addrline1 || "N/A"}</td>
                      <td style={{ padding: "10px" }}>{p.county || "N/A"}</td>
                      <td style={{ padding: "10px", textAlign: "right" }}>
                        {p.parcelsize ? p.parcelsize.toFixed(3) : "N/A"}
                      </td>
                      <td style={{ padding: "10px" }}>{p.zonedcodelocal || "N/A"}</td>
                      <td style={{ padding: "10px" }}>{p.parcelcustomfemaflood || "N/A"}</td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        {p.is_vaccant === 1 ? "✓" : "—"}
                      </td>
                      <td style={{ padding: "10px" }}>{p.listedsale || "Not listed"}</td>
                      <td style={{ padding: "10px", textAlign: "right" }}>
                        {formatCurrency(p.zestimate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ✅ Map View - Appears on Right When Enabled */}
        {showMap && (
          <div style={{ width: "50%", position: "relative", transition: "width 0.3s ease" }}>
            <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />

            {/* Flood Zone Legend */}
            <div
              style={{
                position: "absolute",
                bottom: "10px",
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(255, 255, 255, 0.95)",
                padding: "8px 12px",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)",
                fontSize: "11px",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span style={{ fontWeight: "600", color: "#333", marginRight: "5px" }}>
                FloodZone:
              </span>
              {FLOOD_ZONE_LEGEND.map((item) => (
                <div key={item.zone} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      backgroundColor: item.color,
                      opacity: 0.5,
                      borderRadius: "3px",
                      border: `2px solid ${item.color}`,
                    }}
                  />
                  <span style={{ whiteSpace: "nowrap" }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParcelTableWithMap;